/**
 * Onboarding Checklist Service
 * Manages onboarding progress tracking and step completion
 * Reads onboarding steps from ASSISTANT_ACTIONS registry
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ASSISTANT_ACTION_DEFINITIONS,
  AssistantActionDefinition,
  OnboardingConfig,
  OnboardingTier
} from './assistant-actions.registry';

const STORAGE_KEY = 'sf_onboarding_state';

export interface OnboardingStep {
  actionId: string;
  action: AssistantActionDefinition;
  config: OnboardingConfig;
}

export interface OnboardingProgress {
  totalSteps: number;
  completedSteps: number;
  percentComplete: number;
  currentTier: OnboardingTier;
  nextStep?: OnboardingStep;
  completedStepIds: string[];
  skippedStepIds: string[];
}

export interface OnboardingState {
  startedAt: Date;
  lastActivityAt: Date;
  progress: OnboardingProgress;
  dismissedUntil?: Date;
  completedAt?: Date;
}

@Injectable({ providedIn: 'root' })
export class OnboardingChecklistService {

  private stateSubject = new BehaviorSubject<OnboardingState | null>(null);
  private completedStepsSubject = new BehaviorSubject<Set<string>>(new Set());

  public readonly state$ = this.stateSubject.asObservable();
  public readonly completedSteps$ = this.completedStepsSubject.asObservable();

  public readonly progress$: Observable<OnboardingProgress | null> = this.state$.pipe(
    map(state => state?.progress ?? null)
  );

  public readonly isComplete$: Observable<boolean> = this.progress$.pipe(
    map(progress => progress?.percentComplete === 100)
  );

  public readonly nextStep$: Observable<OnboardingStep | null> = this.progress$.pipe(
    map(progress => progress?.nextStep ?? null)
  );

  private userRole: 'member' | 'manager' | 'admin' = 'member';
  private isAdmin = false;
  private accountData: Record<string, any> = {};
  private userData: Record<string, any> = {};

  // Cached onboarding steps from registry
  private _onboardingSteps: OnboardingStep[] = [];

  constructor() {
    this.loadState();
    this.buildOnboardingSteps();
    // Initialize progress if not loaded from storage
    if (!this.stateSubject.value && this._onboardingSteps.length > 0) {
      this.refreshCompletionStatus();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD STEPS FROM REGISTRY
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Extract actions with onboarding config from registry
   */
  private buildOnboardingSteps(): void {
    this._onboardingSteps = ASSISTANT_ACTION_DEFINITIONS
      .filter(action => action.onboarding)
      .map(action => ({
        actionId: action.id,
        action,
        config: action.onboarding!
      }))
      .sort((a, b) => {
        // Sort by tier priority, then by order within tier
        const tierOrder: Record<OnboardingTier, number> = {
          critical: 0,
          important: 1,
          helpful: 2
        };
        const tierDiff = tierOrder[a.config.tier] - tierOrder[b.config.tier];
        if (tierDiff !== 0) return tierDiff;
        return a.config.order - b.config.order;
      });
  }

  /**
   * Get all onboarding steps (filtered by role)
   */
  getAllOnboardingSteps(): OnboardingStep[] {
    return this._onboardingSteps.filter(step => {
      if (step.config.adminOnly && !this.isAdmin) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get steps for trial mode (quick start subset)
   */
  getTrialSteps(): OnboardingStep[] {
    return this._onboardingSteps.filter(step => {
      if (step.config.adminOnly && !this.isAdmin) {
        return false;
      }
      return step.config.showInTrial === true;
    });
  }

  /**
   * Get steps for full setup (comprehensive onboarding)
   */
  getFullSetupSteps(): OnboardingStep[] {
    return this._onboardingSteps.filter(step => {
      if (step.config.adminOnly && !this.isAdmin) {
        return false;
      }
      return step.config.showInFullSetup === true;
    });
  }

  /**
   * Get steps grouped by tier
   */
  getStepsByTier(): Record<OnboardingTier, OnboardingStep[]> {
    const steps = this.getAllOnboardingSteps();
    return {
      critical: steps.filter(s => s.config.tier === 'critical'),
      important: steps.filter(s => s.config.tier === 'important'),
      helpful: steps.filter(s => s.config.tier === 'helpful')
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize the onboarding service with user/account context
   */
  initialize(
    userRole: 'member' | 'manager' | 'admin',
    accountData: Record<string, any>,
    userData: Record<string, any>
  ): void {
    this.userRole = userRole;
    this.isAdmin = userRole === 'admin';
    this.accountData = accountData;
    this.userData = userData;

    this.refreshCompletionStatus();
  }

  private loadState(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const state = JSON.parse(stored) as OnboardingState;
        state.startedAt = new Date(state.startedAt);
        state.lastActivityAt = new Date(state.lastActivityAt);
        if (state.completedAt) state.completedAt = new Date(state.completedAt);
        if (state.dismissedUntil) state.dismissedUntil = new Date(state.dismissedUntil);
        this.stateSubject.next(state);
        this.completedStepsSubject.next(new Set(state.progress.completedStepIds));
      }
    } catch (e) {
      console.warn('Failed to load onboarding state:', e);
    }
  }

  private saveState(state: OnboardingState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      this.stateSubject.next(state);
    } catch (e) {
      console.warn('Failed to save onboarding state:', e);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPLETION TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  refreshCompletionStatus(): void {
    const steps = this.getAllOnboardingSteps();
    const completed = new Set<string>();

    for (const step of steps) {
      if (this.isStepCompleted(step)) {
        completed.add(step.actionId);
      }
    }

    this.completedStepsSubject.next(completed);
    this.updateProgress(completed);
  }

  private isStepCompleted(step: OnboardingStep): boolean {
    const config = step.config;
    if (!config.completionKey) return false;

    const value = this.getCompletionValue(config.completionKey);

    switch (config.completionCheck) {
      case 'boolean':
        return value === true;
      case 'count':
        const count = typeof value === 'number' ? value : 0;
        return count >= (config.completionThreshold ?? 1);
      case 'exists':
        return value !== null && value !== undefined;
      default:
        return false;
    }
  }

  private getCompletionValue(key: string): any {
    const [source, ...pathParts] = key.split('.');
    const data = source === 'account' ? this.accountData : this.userData;

    let value = data;
    for (const part of pathParts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return undefined;
      }
    }
    return value;
  }

  private updateProgress(completed: Set<string>): void {
    const steps = this.getAllOnboardingSteps();
    const completedStepIds = Array.from(completed);

    // Determine current tier
    let currentTier: OnboardingTier = 'helpful';
    const priorities: OnboardingTier[] = ['critical', 'important', 'helpful'];

    for (const priority of priorities) {
      const tierSteps = steps.filter(s => s.config.tier === priority);
      const tierCompleted = tierSteps.filter(s => completed.has(s.actionId));
      if (tierCompleted.length < tierSteps.length) {
        currentTier = priority;
        break;
      }
    }

    // Find next recommended step
    const nextStep = steps.find(s => !completed.has(s.actionId));

    const progress: OnboardingProgress = {
      totalSteps: steps.length,
      completedSteps: completed.size,
      percentComplete: steps.length > 0 ? Math.round((completed.size / steps.length) * 100) : 0,
      currentTier,
      nextStep,
      completedStepIds,
      skippedStepIds: this.stateSubject.value?.progress.skippedStepIds ?? []
    };

    const state: OnboardingState = {
      startedAt: this.stateSubject.value?.startedAt ?? new Date(),
      lastActivityAt: new Date(),
      progress,
      completedAt: progress.percentComplete === 100 ? new Date() : undefined
    };

    this.saveState(state);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  getProgress(): OnboardingProgress | null {
    return this.stateSubject.value?.progress ?? null;
  }

  isCompleted(actionId: string): boolean {
    return this.completedStepsSubject.value.has(actionId);
  }

  markCompleted(actionId: string): void {
    const completed = new Set(this.completedStepsSubject.value);
    completed.add(actionId);
    this.completedStepsSubject.next(completed);
    this.updateProgress(completed);
  }

  skipStep(actionId: string): void {
    const state = this.stateSubject.value;
    if (!state) return;

    if (!state.progress.skippedStepIds.includes(actionId)) {
      state.progress.skippedStepIds.push(actionId);
      state.lastActivityAt = new Date();
      this.saveState(state);
    }
  }

  dismissFor(hours: number): void {
    const state = this.stateSubject.value;
    if (!state) return;

    const dismissedUntil = new Date();
    dismissedUntil.setHours(dismissedUntil.getHours() + hours);
    state.dismissedUntil = dismissedUntil;
    this.saveState(state);
  }

  shouldShowChecklist(): boolean {
    const state = this.stateSubject.value;
    if (!state) return true;
    if (state.completedAt) return false;
    if (state.dismissedUntil && new Date() < state.dismissedUntil) return false;
    return true;
  }

  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.stateSubject.next(null);
    this.completedStepsSubject.next(new Set());
  }

  getProgressSummary(): string {
    const progress = this.getProgress();
    if (!progress) return '';
    return `${progress.completedSteps} of ${progress.totalSteps} completed`;
  }

  getTierProgress(tier: OnboardingTier): { completed: number; total: number; percent: number } {
    const steps = this.getAllOnboardingSteps().filter(s => s.config.tier === tier);
    const completed = steps.filter(s => this.isCompleted(s.actionId)).length;
    return {
      completed,
      total: steps.length,
      percent: steps.length > 0 ? Math.round((completed / steps.length) * 100) : 0
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TRIAL MODE SPECIFIC
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get progress for trial mode steps only
   */
  getTrialProgress(): OnboardingProgress | null {
    const steps = this.getTrialSteps();
    if (steps.length === 0) return null;

    const completedSet = this.completedStepsSubject.value;
    const completed = steps.filter(s => completedSet.has(s.actionId));
    const completedStepIds = completed.map(s => s.actionId);

    // Determine current tier for trial steps
    let currentTier: OnboardingTier = 'helpful';
    const priorities: OnboardingTier[] = ['critical', 'important', 'helpful'];

    for (const priority of priorities) {
      const tierSteps = steps.filter(s => s.config.tier === priority);
      const tierCompleted = tierSteps.filter(s => completedSet.has(s.actionId));
      if (tierCompleted.length < tierSteps.length) {
        currentTier = priority;
        break;
      }
    }

    // Find next recommended step
    const nextStep = steps.find(s => !completedSet.has(s.actionId));

    return {
      totalSteps: steps.length,
      completedSteps: completed.length,
      percentComplete: Math.round((completed.length / steps.length) * 100),
      currentTier,
      nextStep,
      completedStepIds,
      skippedStepIds: this.stateSubject.value?.progress.skippedStepIds ?? []
    };
  }

  /**
   * Get progress summary for trial mode
   */
  getTrialProgressSummary(): string {
    const progress = this.getTrialProgress();
    if (!progress) return '';
    return `${progress.completedSteps}/${progress.totalSteps}`;
  }

  /**
   * Check if trial onboarding is complete
   */
  isTrialComplete(): boolean {
    const progress = this.getTrialProgress();
    return progress?.percentComplete === 100;
  }
}
